import { useEffect, useId, useSyncExternalStore } from 'react'

/**
 * Which mounted order-form instance on a card may run the selection atom's account
 * normalization write (use-unified-order-form.ts).
 *
 * Several instances can legitimately share one card's selection atom at once — the widget
 * and its Document PIP copy, or the widget and the order form sheet. Each has its own RHF
 * form, and that write copies the instance's OWN in-progress amount into the shared atom; two
 * instances holding different amounts (one typed 80,000, the other still 76910) then rewrite
 * the atom back and forth on every render until React bails ("Maximum update depth").
 *
 * The most recently mounted instance wins — it's the surface the user just opened and is
 * working in — and the previous one takes over again when it unmounts.
 */
const writersByCard = new Map<string, string[]>()
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((listener) => listener())

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useIsSelectionWriter(cardId: string): boolean {
  const token = useId()

  useEffect(() => {
    writersByCard.set(cardId, [...(writersByCard.get(cardId) ?? []), token])
    notify()
    return () => {
      const remaining = (writersByCard.get(cardId) ?? []).filter((t) => t !== token)
      if (remaining.length > 0) writersByCard.set(cardId, remaining)
      else writersByCard.delete(cardId)
      notify()
    }
  }, [cardId, token])

  const isWriter = () => writersByCard.get(cardId)?.at(-1) === token
  return useSyncExternalStore(subscribe, isWriter, isWriter)
}
